import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import {
  GetProductsByIdBatchedQuery,
  ProductIdType,
} from '@notino/shared/definitions/types';

import { IProduct } from '@containers/ProductListing/model';
import { RecommendationPlacement } from '@utils/tracking/recommendationPlacement';
import { RecommendationAttributesInput } from '@utils/tracking/recommendationTracking';

import { dispatchProductsShownEvent } from '../hooks/useDispatchProductsShowEvent';
import { ISectionContainerProps, SectionContainer } from '../SectionContainer';

import { ProductListContent } from './ProductItemsContent';
import { useProductsByIdsBatched } from './useProductsByIdsBatched';

export type VpProductItem = NonNullable<
  GetProductsByIdBatchedQuery['vpProductByIds'][number]
>;

export interface IProductsSectionProps
  extends Omit<ISectionContainerProps, 'children'> {
  productIds: Array<IProduct['id']>;
  idType?: ProductIdType;
  filterProducts?: (product: VpProductItem) => boolean;
  productIdsLoading?: boolean;
  trackProductClick?: boolean;
  trackProductImpression?: boolean;
  attributionToken?: string;
  itemsPerSlide?: number;
  titleClassName?: string;
  listContentClassName?: string;
  isSponsored?: boolean;
  onProductClick?: (productId: string) => void;
  onProductImpression?: (productId: string) => void;
  recommendationPlacement?: RecommendationPlacement;
  recommendationAttributes?: RecommendationAttributesInput;
}

export const ProductsSection: React.FC<IProductsSectionProps> = React.memo(
  ({
    productIds,
    idType,
    filterProducts,
    listLabel = '',
    containerId = '',
    titleId = '',
    productIdsLoading = false,
    titleMessage = null,
    withDivider = false,
    trackProductClick = false,
    trackProductImpression = false,
    attributionToken,
    itemsPerSlide,
    titleClassName,
    listContentClassName,
    isSponsored = false,
    onProductClick,
    onProductImpression,
    recommendationPlacement,
    recommendationAttributes,
  }) => {
    const [ref, inView] = useInView({ triggerOnce: true });

    const { loading, data: rawData } = useProductsByIdsBatched(productIds, {
      idType,
      skip: !inView || productIdsLoading,
      onCompleted: (response) => {
        dispatchProductsShownEvent(
          response?.vpProductByIds
            .filter((p): p is NonNullable<typeof p> => p !== null)
            .filter((p) => !filterProducts || filterProducts(p))
            .map((p) => ({ ...p, webId: p.id! })),
          {
            section: listLabel,
          }
        );
      },
    });

    const data = React.useMemo(() => {
      if (!rawData || !filterProducts) {
        return rawData;
      }
      return {
        ...rawData,
        vpProductByIds: rawData.vpProductByIds?.filter(
          (p) => !!p && filterProducts(p)
        ),
      };
    }, [rawData, filterProducts]);

    const noProducts = !loading && !data?.vpProductByIds?.length;
    if (noProducts && inView && !productIdsLoading) {
      return null;
    }

    return (
      <SectionContainer
        containerId={containerId}
        key={containerId}
        withDivider={withDivider}
        titleMessage={titleMessage ?? undefined}
        titleId={titleId}
        ref={ref}
        titleClassName={titleClassName}
      >
        <ProductListContent
          className={listContentClassName}
          itemsPerSlide={itemsPerSlide}
          loading={loading || productIdsLoading}
          inView={inView}
          data={data}
          trackProductClick={trackProductClick}
          trackProductImpression={trackProductImpression}
          listLabel={listLabel}
          attributionToken={attributionToken}
          isSponsored={isSponsored}
          onProductClick={onProductClick}
          onProductImpression={onProductImpression}
          recommendationPlacement={recommendationPlacement}
          recommendationAttributes={recommendationAttributes}
        />
      </SectionContainer>
    );
  }
);
